import type { Maybe, Stylable } from '@core/utils/types'
import { cn } from 'cn'
import type { AnchorHTMLAttributes } from 'react'
import Markdown, { type Components } from 'react-markdown'
import remarkGfm from 'remark-gfm'

const REMARK_PLUGINS = [remarkGfm]

const MARKDOWN_STYLES = [
  'max-w-none text-foreground text-sm leading-relaxed',
  '[&>*+*]:mt-3',
  '[&_h1]:font-semibold [&_h1]:text-foreground [&_h1]:text-lg',
  '[&_h2]:font-semibold [&_h2]:text-base [&_h2]:text-foreground',
  '[&_h3]:font-medium [&_h3]:text-foreground [&_h3]:text-sm',
  '[&_p]:text-foreground',
  '[&_ul]:list-disc [&_ul]:pl-5 [&_ul]:text-foreground',
  '[&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:text-foreground',
  '[&_li]:my-1',
  '[&_li>ol]:mt-1 [&_li>ul]:mt-1',
  '[&_a]:text-primary [&_a]:underline [&_a]:underline-offset-2',
  '[&_strong]:font-semibold [&_strong]:text-foreground',
  '[&_code]:rounded [&_code]:bg-muted [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-xs',
  '[&_pre]:overflow-x-auto [&_pre]:rounded-md [&_pre]:bg-muted [&_pre]:p-3',
  '[&_pre_code]:bg-transparent [&_pre_code]:p-0',
  '[&_table]:w-full [&_table]:border-collapse [&_table]:text-sm',
  '[&_th]:border [&_th]:border-border [&_th]:bg-muted/50 [&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_th]:font-medium',
  '[&_td]:border [&_td]:border-border [&_td]:px-3 [&_td]:py-2 [&_td]:text-foreground',
  '[&_blockquote]:border-border [&_blockquote]:border-l-2 [&_blockquote]:pl-4 [&_blockquote]:text-muted-foreground [&_blockquote]:italic',
  '[&_hr]:border-border',
].join(' ')

export const markdownLinkComponent = ({
  children,
  href,
  ...props
}: AnchorHTMLAttributes<HTMLAnchorElement>) => (
  <a {...props} href={href} target='_blank' rel='noopener noreferrer'>
    {children}
  </a>
)

const MARKDOWN_COMPONENTS = {
  a: markdownLinkComponent,
}

export type MarkdownBodyProps = Stylable & {
  markdown: Maybe<string>
  components?: Pick<Components, 'a'>
}

export function MarkdownBody({
  markdown,
  components,
  className,
}: MarkdownBodyProps) {
  if (!markdown) return null

  return (
    <Markdown
      remarkPlugins={REMARK_PLUGINS}
      components={{ ...MARKDOWN_COMPONENTS, ...components }}
      className={cn(MARKDOWN_STYLES, className)}
    >
      {markdown}
    </Markdown>
  )
}
